import { useCallback } from 'react';
import { useSetAtom } from 'jotai';
import {
  FileUploadStatusItem,
  fileUploadStatusCollapsedAtom,
  fileUploadStatusItemsAtom,
  fileUploadStatusVisibleAtom,
} from '@/recoil/atom/file-upload-status';

const IMAGE_EXTENSIONS = new Set([
  'jpg',
  'jpeg',
  'png',
  'gif',
  'bmp',
  'svg',
  'webp',
]);

const getExtension = (name: string): string => {
  const idx = name.lastIndexOf('.');
  return idx < 0 ? '' : name.slice(idx + 1).toLowerCase();
};

const isImageFileName = (fileName: string): boolean =>
  IMAGE_EXTENSIONS.has(getExtension(fileName));

let counter = 0;
const generateId = (): string => {
  counter += 1;
  return `fu-${Date.now().toString(36)}-${counter}`;
};

const createPreviewUrl = (
  fileName: string,
  file?: File,
): string | undefined => {
  if (!file) return undefined;
  if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') {
    return undefined;
  }
  if (!isImageFileName(fileName)) return undefined;
  try {
    return URL.createObjectURL(file);
  } catch {
    return undefined;
  }
};

const revokePreviewUrl = (url?: string) => {
  if (!url) return;
  if (typeof URL === 'undefined' || typeof URL.revokeObjectURL !== 'function') {
    return;
  }
  try {
    URL.revokeObjectURL(url);
  } catch {
    // noop
  }
};

/**
 * 어디서든 호출 가능한 파일 업로드 상태 모달 API.
 * - 글로벌 단일 인스턴스의 모달이 화면 우하단에 떠 활동 로그를 표시
 * - 첫 addUploading / addError 시 모달이 열리고, removeItem으로 0건이 되면 자동으로 닫힘
 * - 업로드/실 API 통합은 [4/4] — 본 훅은 표시 상태 관리만 담당
 */
export const useFileUploadStatus = () => {
  const setItems = useSetAtom(fileUploadStatusItemsAtom);
  const setVisible = useSetAtom(fileUploadStatusVisibleAtom);
  const setCollapsed = useSetAtom(fileUploadStatusCollapsedAtom);

  const addUploading = useCallback(
    (fileName: string, file?: File): string => {
      const id = generateId();
      const previewUrl = createPreviewUrl(fileName, file);
      const next: FileUploadStatusItem = {
        id,
        fileName,
        status: 'uploading',
        file,
        previewUrl,
      };
      setItems((prev) => [next, ...prev]);
      setVisible(true);
      return id;
    },
    [setItems, setVisible],
  );

  const setSuccess = useCallback(
    (id: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, status: 'success' } : item,
        ),
      );
    },
    [setItems],
  );

  const setError = useCallback(
    (id: string, message: string) => {
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, status: 'error', message } : item,
        ),
      );
    },
    [setItems],
  );

  const addError = useCallback(
    (fileName: string, message: string): string => {
      const id = generateId();
      const next: FileUploadStatusItem = {
        id,
        fileName,
        status: 'error',
        message,
      };
      setItems((prev) => [next, ...prev]);
      setVisible(true);
      return id;
    },
    [setItems, setVisible],
  );

  const removeItem = useCallback(
    (id: string) => {
      setItems((prev) => {
        const target = prev.find((it) => it.id === id);
        revokePreviewUrl(target?.previewUrl);
        const next = prev.filter((it) => it.id !== id);
        if (next.length === 0) setVisible(false);
        return next;
      });
    },
    [setItems, setVisible],
  );

  // 업로드를 발생시킨 화면(작성폼)이 닫힐 때 이걸 호출해 모달을 정리한다.
  // 모달이 글로벌이라 안 지우면 화면 전환 후에도 items 가 남아
  // 조회화면 위에 '업로드 완료'가 계속 떠 있게 된다.
  const clearAll = useCallback(() => {
    setItems((prev) => {
      prev.forEach((it) => revokePreviewUrl(it.previewUrl));
      return [];
    });
    setVisible(false);
    setCollapsed(false);
  }, [setItems, setVisible, setCollapsed]);

  return { addUploading, setSuccess, setError, addError, removeItem, clearAll };
};

export default useFileUploadStatus;
